import * as Cookies from "js-cookie";
import Player from '@vimeo/player';
import { ModalManager, ModalPriority } from "./modal_manager";
import {
  clearAdvertisementVideoLogFlushTimer,
  createAdvertisementLogWithRetries,
  flushAdvertisementVideoLogPlayback,
  registerAdvertisementVideoPlaybackLog,
  releaseAdvertisementLogPageContextIfCurrent,
} from './ad_modal/advertisement_log_client';

function adIdFromModalDialog(modalDialog) {
  return modalDialog.dataset.advertisementId;
}

function buildPerAdSuppressionCookieName(adId) {
  return `ad_suppression_${adId}`;
}

// Must match Advertisement::SESSION_SUPPRESSION_COOKIE_NAME (app/models/advertisement.rb)
const SESSION_SUPPRESSION_COOKIE_NAME = 'ad_session_suppression';

// After any ad is shown, suppress all scholarship ads for this window (issue #1482; UX vs inventory balance)
const SESSION_SUPPRESSION_MINUTES = 20;

// Per-ad cookie TTL: long enough for rotation across many active ads (issue #1482)
const PER_AD_SUPPRESSION_DAYS = 7;

const cookieOptionsSameSiteLax = { sameSite: 'Lax' };

function bakeAdSessionSuppressionCookie() {
  const expires = new Date(Date.now() + SESSION_SUPPRESSION_MINUTES * 60 * 1000);

  Cookies.set(SESSION_SUPPRESSION_COOKIE_NAME, 'suppressed', {
    expires,
    ...cookieOptionsSameSiteLax,
  });
}

// Per-ad suppression after modal close (Scholarship ad model: balance impressions vs UX)
function bakePerAdSuppressionCookie(adId) {
  const cookieName = buildPerAdSuppressionCookieName(adId);

  Cookies.set(cookieName, 'suppressed', {
    expires: PER_AD_SUPPRESSION_DAYS,
    ...cookieOptionsSameSiteLax,
  });
}

/**
 * Disable the Escape key to prevent immediate dismissal of the advertisement modal
 * <dialog> `closedBy` attribute handles this for browsers that support it (Chrome 134+, Firefox 141+)
 * For other browsers (Safari, etc.), we still need to prevent the default behavior of the Escape key
 *
 * @param {HTMLDialogElement} modal
 */
function preventEscapeToCloseModal(modal) {
  const closedBySupported = 'closedBy' in HTMLDialogElement.prototype;
  if (closedBySupported) return;

  modal.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') event.preventDefault();
  }, { capture: true });
}

function attachModalHandlers(modal) {
  // Escapeキーでのモーダル閉鎖を防止
  preventEscapeToCloseModal(modal);

  // ✗ボタンクリックで閉じる
  const closeButton = modal.querySelector(".ad-modal-close");
  closeButton?.addEventListener("click", () => modal.close());

  // close時にcookieに書き込む
  modal.addEventListener("close", () => {
    const adId = adIdFromModalDialog(modal);

    bakePerAdSuppressionCookie(adId);
    bakeAdSessionSuppressionCookie();
  });
}

function isImageAdvertisementModal(element) {
  const modalVideoIframe = element.querySelector('iframe.ad-modal-video[id^="ad-video-"]');
  if (modalVideoIframe) return false;

  const modalDisplayImage = element.querySelector('img.ad-modal-image');
  return (modalDisplayImage != null);
}

/**
 * 広告モーダル用 DialogAdapter。showModal() 直前に ad 動画の再生を試みる。
 */
class AdModalDialogAdapter {
  constructor(element, priority, player, { onClose, adId } = {}) {
    this.element = element;
    this.priority = priority;
    this.player = player;
    this.onClose = onClose;
    this.adId = adId;
    this.impressionLogged = false;
  }

  open() {
    if (this.player) {
      this.player.play().catch(error => {
        switch (error.name) {
          case 'NotAllowedError':
            // [iOS] auto play failed due to low power mode, etc.
            this.player.pause().then(() => this.player.unload());
            break;
          default:
            break;
        }
      });
    }
    return new Promise((resolve) => {
      this.element.addEventListener("close", async () => {
        if (this.player) {
          clearAdvertisementVideoLogFlushTimer(this.player);
          await flushAdvertisementVideoLogPlayback(this.player);
          releaseAdvertisementLogPageContextIfCurrent(this.player);
        }
        this.onClose?.(); // openAdModal の resolve 用
        resolve();        // ModalManager の dispatch 用
      }, { once: true });
      this.element.showModal();

      // Image ads only: fire a single log-advertisement create after the dialog is shown (video uses Vimeo play logging).
      if (isImageAdvertisementModal(this.element) && !this.impressionLogged) {
        this.impressionLogged = true;
        void createAdvertisementLogWithRetries(this.adId);
      }
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const modal = document.querySelector(".ad-modal");
  if (!modal) return;

  const adId = adIdFromModalDialog(modal);
  if (!adId) return;

  attachModalHandlers(modal);

  function initialize() {
    const iframe = modal.querySelector('iframe.ad-modal-video[id^="ad-video-"]');
    if (!iframe) return Promise.resolve(null);

    const player = new Player(iframe);
    return player.ready()
      // mute (auto play policy based)
      .then(() => player.setMuted(true))
      .then(() => {
        registerAdvertisementVideoPlaybackLog(player, adId);
        return player;
      })
      .catch(() => null);
  }

  /**
   * 広告モーダルを表示し、閉じられたら resolve する Promise を返す。
   *
   * @returns {Promise<void>}
   */
  function openAdModal() {
    return new Promise((resolve) => {
      const enqueueAdModal = (player) => {
        const adapter = new AdModalDialogAdapter(
          modal,
          ModalPriority.ADVERTISEMENT,
          player,
          { onClose: () => resolve(), adId }
        );
        ModalManager.enqueue(adapter);
      };

      initialize().then(enqueueAdModal);
    });
  }

  /**
   * 本編再生を一時停止し、広告モーダルを表示する。閉じられたら本編再生を再開する。
   * vimeo.js の本編再生インターセプト時に adModal:requestIntercept イベントのハンドラとして呼ばれる。
   *
   * @param {Object} mainPlayer - Vimeo Player インスタンス（pause, unload, play を持つ）
   * @returns {Promise<void>}
   */
  async function interceptWithAdModal(mainPlayer) {
    mainPlayer.intercepted = true;
    // On failure: clear intercepted, then rethrow so the caller (adModal:requestIntercept handler) can log
    try {
      await mainPlayer.pause();   // 再生を停止
      await mainPlayer.unload();  // 再生前の状態(サムネイル表示)に戻す
      await openAdModal();        // 広告モーダルを表示
      await mainPlayer.play();    // 広告モーダル表示が終わったら本編再生を再開
    } catch (e) {
      mainPlayer.intercepted = false;
      throw e;
    }
  }

  /**
   * Handles custom event `adModal:requestIntercept` (dispatched from vimeo.js `requestAdModalIntercept`)
   *
   * `event.detail` must include:
   * - `player` (required) — Vimeo Player to intercept
   * - `resolve` (required) — this handler must invoke it exactly once on every path (e.g. in `finally`),
   *   including success, failure, or early return
   */
  document.addEventListener("adModal:requestIntercept", async (event) => {
    const { player, resolve } = event.detail;
    try {
      // 有効な表示抑制cookieがあれば広告モーダルを表示しない
      const sessionSuppressionCookie = Cookies.get(SESSION_SUPPRESSION_COOKIE_NAME);
      if (sessionSuppressionCookie) return;

      const perAdSuppressionCookie = Cookies.get(buildPerAdSuppressionCookieName(adId));
      if (perAdSuppressionCookie) return;

      await interceptWithAdModal(player);
    } catch (e) {
      // e.g. Vimeo Player API rejections, autoplay policy (NotAllowedError), network errors
      console.error('[ad_modal] intercept failed', e);
    } finally {
      resolve();
    }
  });
});
